import { z } from "zod"

/**
 * Единая управляемая выдача главной (fd-feed-01).
 *
 * Один контракт на веб И приложение: админ ведёт упорядоченный список
 * типизированных блоков, оба клиента рендерят одно и то же. Мобильное
 * приложение уже написано под проводную форму ниже (GET /feed/blocks).
 */

/** Плейсмент выдачи. Пока только главная. */
export const FeedPlacementSchema = z.enum(["home"])
export type FeedPlacement = z.infer<typeof FeedPlacementSchema>

/** Ось аудитории. NULL/undefined — блок общий для всех сегментов. */
export const FeedSegmentSchema = z.enum(["womens", "mens", "kids"])
export type FeedSegment = z.infer<typeof FeedSegmentSchema>

/**
 * Верхняя ось «департамент»: «Одежда» (сегментируется по полу), «Красота» и
 * «Дом» (без гендерного деления) — корни дерева категорий v2. Блок принадлежит
 * ровно одному департаменту; segment внутри «Красоты» и «Дома» всегда NULL.
 */
export const FeedDepartmentSchema = z.enum(["clothes", "home", "beauty"])
export type FeedDepartment = z.infer<typeof FeedDepartmentSchema>

/** Тип блока. Совпадает с вокабуляром клиента (мобилка/веб). */
export const FeedBlockKindSchema = z.enum([
  "campaign",
  "ideaRail",
  "categoryRow",
  "collectionMosaic",
  "productRail",
  "photoBanner",
  "personalFeed",
])
export type FeedBlockKind = z.infer<typeof FeedBlockKindSchema>

// --- Проводная форма (то, что уходит клиенту на GET /feed/blocks) -----------

/** Плитка (categoryRow / collectionMosaic / ideaRail). */
export type FeedTileWire = {
  id: string
  imageUrl: string
  label?: string
  categoryId?: string
  collectionSlug?: string
}

/** Слайд (campaign / photoBanner). */
export type FeedSlideWire = {
  id: string
  imageUrl: string
  title?: string
  description?: string
  categoryId?: string
  collectionSlug?: string
}

/** Один блок выдачи в проводной форме. */
export type FeedBlockWire = {
  id: string
  kind: FeedBlockKind
  title?: string
  description?: string
  action?: { kind?: "link" | "chevron"; label?: string }
  items?: FeedTileWire[]
  slides?: FeedSlideWire[]
  productIds?: string[]
}

/** Ответ GET /feed/blocks. */
export type FeedBlocksResponse = { blocks: FeedBlockWire[] }

// --- Админ-модель (управление блоками) -------------------------------------

/** Полная запись блока для админки. */
export type FeedBlockAdmin = {
  id: string
  placement: FeedPlacement
  department: FeedDepartment
  segment: FeedSegment | null
  kind: FeedBlockKind
  position: number
  title: string | null
  description: string | null
  action: { kind?: "link" | "chevron"; label?: string } | null
  payload: { items?: FeedTileWire[]; slides?: FeedSlideWire[]; productIds?: string[] }
  published: boolean
  createdAt: string
  updatedAt: string
}

const tileInput = z.object({
  id: z.string().trim().min(1).max(64),
  imageUrl: z.string().trim().url().max(2000),
  label: z.string().trim().max(200).optional(),
  categoryId: z.string().trim().max(64).optional(),
  collectionSlug: z.string().trim().max(200).optional(),
})

const slideInput = z.object({
  id: z.string().trim().min(1).max(64),
  imageUrl: z.string().trim().url().max(2000),
  title: z.string().trim().max(200).optional(),
  description: z.string().trim().max(500).optional(),
  categoryId: z.string().trim().max(64).optional(),
  collectionSlug: z.string().trim().max(200).optional(),
})

const actionInput = z
  .object({
    kind: z.enum(["link", "chevron"]).optional(),
    label: z.string().trim().max(120).optional(),
  })
  .nullable()

const payloadInput = z.object({
  items: z.array(tileInput).max(24).optional(),
  slides: z.array(slideInput).max(12).optional(),
  productIds: z.array(z.string().trim().min(1).max(64)).max(48).optional(),
})

/** Создание блока. */
export const CreateFeedBlockSchema = z.object({
  placement: FeedPlacementSchema.default("home"),
  department: FeedDepartmentSchema.default("clothes"),
  segment: FeedSegmentSchema.nullable().default(null),
  kind: FeedBlockKindSchema,
  title: z.string().trim().max(200).nullable().optional(),
  description: z.string().trim().max(500).nullable().optional(),
  action: actionInput.optional(),
  payload: payloadInput.default({}),
  published: z.boolean().default(true),
})
export type CreateFeedBlockRequest = z.infer<typeof CreateFeedBlockSchema>

/** Частичное обновление блока. */
export const UpdateFeedBlockSchema = z.object({
  department: FeedDepartmentSchema.optional(),
  segment: FeedSegmentSchema.nullable().optional(),
  title: z.string().trim().max(200).nullable().optional(),
  description: z.string().trim().max(500).nullable().optional(),
  action: actionInput.optional(),
  payload: payloadInput.optional(),
  published: z.boolean().optional(),
})
export type UpdateFeedBlockRequest = z.infer<typeof UpdateFeedBlockSchema>

/** Переупорядочивание: массив id в нужном порядке (в рамках placement). */
export const ReorderFeedBlocksSchema = z.object({
  placement: FeedPlacementSchema.default("home"),
  orderedIds: z.array(z.string().trim().min(1)).max(200),
})
export type ReorderFeedBlocksRequest = z.infer<typeof ReorderFeedBlocksSchema>

// --- Персональная лента товаров (п.16 правок 27.10) -------------------------

/**
 * Настройки персональной ленты главной: переключатель и веса сигналов. Правятся
 * в консоли («Выдача»), хранятся ключом `feed.personal` в `settings`.
 *
 * Веса — «во сколько раз этот сигнал говорит об интересе сильнее одного
 * просмотра карточки». Показ без клика — слабый негатив (вес отрицательный и
 * действует только на сам товар, не на категорию).
 */
export const PersonalFeedWeightsSchema = z.object({
  purchase: z.number().min(0).max(100),
  cart: z.number().min(0).max(100),
  favorite: z.number().min(0).max(100),
  shopFollow: z.number().min(0).max(100),
  view: z.number().min(0).max(100),
  impression: z.number().min(-10).max(0),
})
export type PersonalFeedWeights = z.infer<typeof PersonalFeedWeightsSchema>

export const PersonalFeedSettingsSchema = z.object({
  enabled: z.boolean(),
  weights: PersonalFeedWeightsSchema,
  /** Период полураспада сигнала, дней: через столько дней сигнал весит вдвое меньше. */
  halfLifeDays: z.number().min(1).max(365),
  /** Доля «исследования» — товары вне профиля, чтобы лента не замыкалась (0-0,5). */
  explorationShare: z.number().min(0).max(0.5),
  /** Не больше стольких товаров одного магазина подряд. */
  maxSameShopInRow: z.number().int().min(1).max(10),
})
export type PersonalFeedSettings = z.infer<typeof PersonalFeedSettingsSchema>

export const PersonalFeedSettingsPatchSchema = PersonalFeedSettingsSchema.extend({
  weights: PersonalFeedWeightsSchema.partial(),
}).partial()
export type PersonalFeedSettingsPatch = z.infer<typeof PersonalFeedSettingsPatchSchema>

export const DEFAULT_PERSONAL_FEED_SETTINGS: PersonalFeedSettings = {
  enabled: true,
  weights: { purchase: 10, cart: 5, favorite: 4, shopFollow: 3, view: 1.5, impression: -0.15 },
  halfLifeDays: 21,
  explorationShare: 0.15,
  maxSameShopInRow: 2,
}
